import {useEffect, useMemo, useState} from "react";
import {Link, Outlet, useParams} from "react-router";
import {AccessibleCampaign} from "@/auth/User.ts";
import {getMyCampaignBySlug} from "@/api/UserApi.ts";
import {CampaignContext, CampaignContextValue} from "@/campaigns/CampaignContextHooks.ts";
import PlayerViewProvider from "@/player/PlayerViewProvider.tsx";
import {ActiveEncounterProvider} from "@/encounter/ActiveEncounterProvider.tsx";
import {ModalProvider} from "@/modal/ModalProvider.tsx";

type LoadState =
    | {slug: string; status: "loading"}
    | {slug: string; status: "loaded"; campaign: AccessibleCampaign}
    | {slug: string; status: "error"};

export function CampaignBoundary() {
    const {campaignSlug = ""} = useParams();
    const [state, setState] = useState<LoadState>({slug: campaignSlug, status: "loading"});

    useEffect(() => {
        let cancelled = false;
        getMyCampaignBySlug(campaignSlug)
            .then(campaign => {
                if (!cancelled) setState({slug: campaignSlug, status: "loaded", campaign});
            })
            .catch(() => {
                if (!cancelled) setState({slug: campaignSlug, status: "error"});
            });
        return () => { cancelled = true; };
    }, [campaignSlug]);

    if (state.slug !== campaignSlug || state.status === "loading") {
        return <main className="grid min-h-full place-content-center"><p role="status">Kampagne wird geladen…</p></main>;
    }
    if (state.status === "error") {
        return <main className="grid min-h-full place-content-center gap-2 text-center">
            <title>Kampagne nicht gefunden | DireDawn</title>
            <h1 className="text-xl font-medium">Kampagne nicht gefunden</h1>
            <Link className="underline" to="/">Zur Kampagnenauswahl</Link>
        </main>;
    }

    return <LoadedCampaignBoundary campaign={state.campaign}/>;
}

function LoadedCampaignBoundary({campaign}: {
    campaign: AccessibleCampaign;
}) {
    const value = useMemo<CampaignContextValue>(() => ({
        campaign,
        role: campaign.role,
        isGameMaster: campaign.role === "GAME_MASTER",
        campaignPath: path => `/campaigns/${campaign.slug}${path ? `/${path.replace(/^\/+/, "")}` : ""}`
    }), [campaign]);

    return <CampaignContext.Provider value={value}>
        <ModalProvider key={campaign.id}>
            <ActiveEncounterProvider>
                <PlayerViewProvider>
                    <Outlet/>
                </PlayerViewProvider>
            </ActiveEncounterProvider>
        </ModalProvider>
    </CampaignContext.Provider>;
}
